import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { changeAuditorPassword } from '../services/passwordService';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({
  user,
  auditorDocId,
  keyMaterial,
  onClose,
  onSuccess,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  const passwordsMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setLoading(true);
    try {
      const updated = await changeAuditorPassword({
        user,
        auditorDocId,
        keyMaterial,
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onSuccess?.(updated);
      setDone(true);
      closeTimerRef.current = window.setTimeout(() => onClose(), 1400);
    } catch (err) {
      setError(err.message || 'Failed to change password.');
      setCurrentPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={done ? 'Password updated' : 'Change password'}
      onClose={onClose}
      dismissible={!loading}
      describedById={done ? 'change-password-done' : 'change-password-help'}
    >
      {done ? (
        <div className="change-password-done" id="change-password-done" role="status" aria-live="polite">
          <p>Your password has been changed.</p>
          <div className="change-password-actions">
            <button type="button" className="change-password-submit" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="change-password-form" onSubmit={handleSubmit} aria-busy={loading}>
          <p id="change-password-help" className="change-password-help">
            Enter your current password, then choose a new one.
          </p>

          <div className="form-group">
            <label htmlFor="current-password">Current password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              id="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
              data-modal-initial-focus
            />
          </div>

          <div className="form-group">
            <label htmlFor="new-password">New password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              disabled={loading}
              aria-describedby="new-password-hint"
            />
            <p id="new-password-hint" className="form-hint">
              At least 6 characters.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-new-password">Confirm new password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              id="confirm-new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              disabled={loading}
              aria-invalid={passwordsMismatch}
            />
            {passwordsMismatch && (
              <p className="form-hint form-hint--error" role="status">
                Passwords do not match yet.
              </p>
            )}
          </div>

          <button
            type="button"
            className="change-password-toggle"
            onClick={() => setShowPasswords((v) => !v)}
            aria-pressed={showPasswords}
            disabled={loading}
          >
            {showPasswords ? 'Hide passwords' : 'Show passwords'}
          </button>

          {error && (
            <div className="change-password-error" role="alert">
              {error}
            </div>
          )}

          <div className="change-password-actions">
            <button
              type="button"
              className="ui-btn ui-btn--ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="change-password-submit"
              disabled={loading || passwordsMismatch}
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ChangePasswordModal;
